import { useRef, useState } from "react";
import styled from "styled-components";
import { seoProfileApi, type StockImage } from "../../../services/seo";

type PickedImage = { name: string; contentType: string; dataUrl: string };

type Props = {
  keyword: string;
  city?: string;
  locationHint?: string;
  onPick: (image: PickedImage) => void;
};

const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const resolveCity = (city?: string, locationHint?: string) => {
  if (city && city.trim()) return city.trim();
  if (locationHint && /\sem\s/i.test(locationHint)) {
    return locationHint.split(/\sem\s/i).pop()?.trim() || "";
  }
  return "";
};

export const buildQuery = (keyword: string, city?: string, locationHint?: string) => {
  let q = keyword;
  const resolved = resolveCity(city, locationHint);
  if (resolved) {
    q = q.replace(new RegExp(`\\b${escapeRegex(resolved)}\\b`, "gi"), " ");
  }
  q = q
    .replace(/\bem\b/gi, " ")
    .replace(/[-–·,/]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return q || keyword;
};

const Wrapper = styled.div`
  display: grid;
  gap: 8px;
  margin-top: 8px;
`;

const Bar = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const SearchRow = styled.div`
  align-items: center;
  display: flex;
  gap: 6px;
  width: 100%;
`;

const SearchInput = styled.input`
  border: 1px solid #c7d6f0;
  border-radius: 8px;
  color: #111827;
  flex: 1;
  font-size: 12px;
  min-width: 0;
  outline: none;
  padding: 6px 10px;
  &:focus {
    border-color: #1558d6;
    box-shadow: 0 0 0 2px #1558d620;
  }
`;

const LinkButton = styled.button`
  background: #eef3fc;
  border: 1px solid #c7d6f0;
  border-radius: 8px;
  color: #1558d6;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  white-space: nowrap;
  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

const Hint = styled.span`
  color: #6b7280;
  font-size: 11px;
`;

const Grid = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
`;

const Thumb = styled.button`
  aspect-ratio: 1 / 1;
  background: #f3f5f8;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  overflow: hidden;
  padding: 0;
  position: relative;
  &:hover {
    border-color: #1558d6;
  }
  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
  img {
    height: 100%;
    object-fit: cover;
    width: 100%;
    display: block;
  }
`;

const ErrorText = styled.span`
  color: #b3261e;
  font-size: 11px;
`;

export function StockImageSuggestions({ keyword, city, locationHint, onPick }: Props) {
  const autoQuery = buildQuery(keyword, city, locationHint);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState<StockImage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pickingId, setPickingId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState(autoQuery);
  const [activeQuery, setActiveQuery] = useState(autoQuery);
  const [page, setPage] = useState(1);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = async (query: string, pg: number) => {
    setLoading(true);
    setError(null);
    try {
      const results = await seoProfileApi.searchStockImages(query, 9, pg);
      setImages(results);
      if (results.length === 0) {
        setError("Nenhuma sugestão encontrada. Tente outro termo.");
      }
    } catch {
      setError("Não foi possível carregar sugestões agora.");
    } finally {
      setLoading(false);
    }
  };

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && images.length === 0 && !loading) {
      setSearchTerm(autoQuery);
      setActiveQuery(autoQuery);
      setPage(1);
      await load(autoQuery, 1);
    }
  };

  const search = async () => {
    const q = searchTerm.trim() || autoQuery;
    setActiveQuery(q);
    setPage(1);
    await load(q, 1);
  };

  const next = async () => {
    const pg = page + 1;
    setPage(pg);
    await load(activeQuery, pg);
  };

  const pick = async (img: StockImage) => {
    setPickingId(img.id);
    setError(null);
    try {
      const result = await seoProfileApi.stockImageToDataUrl(img.fullUrl);
      onPick({ name: result.name, contentType: result.contentType, dataUrl: result.dataUrl });
    } catch {
      setError("Não foi possível usar esta foto. Tente outra.");
    } finally {
      setPickingId(null);
    }
  };

  return (
    <Wrapper>
      <Bar>
        <LinkButton type="button" onClick={toggle} disabled={loading}>
          {open ? "Ocultar sugestões" : "Sugestões profissionais"}
        </LinkButton>
        <Hint>Fotos reais (Pexels) — clique para usar; você pode trocar depois.</Hint>
      </Bar>

      {open && (
        <SearchRow>
          <SearchInput
            ref={inputRef}
            type="text"
            value={searchTerm}
            placeholder="Buscar fotos…"
            disabled={loading}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
          />
          <LinkButton type="button" onClick={search} disabled={loading}>
            Buscar
          </LinkButton>
          <LinkButton type="button" onClick={next} disabled={loading || images.length === 0}>
            Próximas
          </LinkButton>
        </SearchRow>
      )}

      {error && <ErrorText>{error}</ErrorText>}

      {open && images.length > 0 && (
        <Grid>
          {images.map((img) => (
            <Thumb
              key={img.id}
              type="button"
              title={img.alt || img.photographer || "Foto"}
              disabled={pickingId !== null}
              onClick={() => pick(img)}
            >
              <img src={img.thumbUrl} alt={img.alt || "Sugestão de foto"} loading="lazy" />
            </Thumb>
          ))}
        </Grid>
      )}
    </Wrapper>
  );
}
